import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Upload, Plus, X, ImageIcon, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function TemplateCreateModal({ template, open, onOpenChange }) {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [selected, setSelected] = useState([]); // [{url, thumb}]
  const [creations, setCreations] = useState([]);
  const [userPrompt, setUserPrompt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const maxRefs = (template?.input_type === "images" || template?.input_type === "images_text") ? 6 : 1;
  const needsText = template?.input_type === "text" || template?.input_type === "images_text";
  const showText = needsText || template?.allow_user_prompt;
  const needsImages = ["image", "images", "images_text"].includes(template?.input_type);
  const bypass = user?.role === "admin" && user?.bypass_credits;

  useEffect(() => {
    if (open) {
      setSelected([]); setUserPrompt("");
      api.get("/images").then(({ data }) => setCreations((data || []).slice(0, 24))).catch(() => {});
    }
  }, [open]);

  const toUpload = async (dataUrl) => {
    const { data } = await api.post("/uploads/image", { image_base64: dataUrl, origin_url: window.location.origin });
    return data.url;
  };

  const addFromDevice = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files.slice(0, maxRefs - selected.length)) {
      try {
        const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
        const url = await toUpload(b64);
        setSelected((s) => (maxRefs === 1 ? [{ url, thumb: b64 }] : [...s, { url, thumb: b64 }]));
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false); e.target.value = "";
  };

  const addFromCreation = async (c) => {
    if (selected.length >= maxRefs && maxRefs > 1) return;
    setUploading(true);
    try {
      const dataUrl = `data:image/png;base64,${c.image_base64}`;
      const url = await toUpload(dataUrl);
      setSelected((s) => (maxRefs === 1 ? [{ url, thumb: dataUrl }] : [...s, { url, thumb: dataUrl }]));
    } catch { toast.error("Could not add that creation"); }
    setUploading(false);
  };

  const generate = async () => {
    if (needsImages && !selected.length) { toast.error("Add at least one image"); return; }
    if (needsText && !userPrompt.trim()) { toast.error("Please add a text prompt"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/templates/${template.id}/generate`, {
        image_urls: selected.map((s) => s.url),
        user_prompt: userPrompt,
        origin_url: window.location.origin,
      });
      if (data.type === "image") toast.success("Created! Opening your creations…");
      else toast.success("Generating your video — see the Creations page.");
      refreshUser?.();
      onOpenChange(false);
      navigate("/creations");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    } finally { setBusy(false); }
  };

  if (!template) return null;
  const costLabel = bypass ? "Free (admin)" : `${template.credit_cost}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-ink-900 border-zinc-800 text-[#F0E9DD] max-w-lg p-0 overflow-hidden" data-testid="template-create-modal">
        <div className="p-5 border-b border-white/10">
          <DialogTitle className="font-heading font-black uppercase tracking-tight text-xl">{template.title}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs mt-1">{template.output_type === "video" ? "Create Video" : "Create Image"} · one-tap</DialogDescription>
        </div>

        <div className="p-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {needsImages && (
          <div>
            <div className="label-mono mb-2">Upload {maxRefs > 1 ? "images" : "image"}</div>
            <div className="flex flex-wrap gap-2">
              {selected.map((s, i) => (
                <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-zinc-700">
                  <img src={s.thumb} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setSelected((x) => x.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-ink-950/80 p-0.5"><X size={12} /></button>
                </div>
              ))}
              {selected.length < maxRefs && (
                <label className="w-20 h-20 border border-dashed border-gold/50 rounded-lg flex items-center justify-center text-gold hover:bg-[#1a140d] cursor-pointer" data-testid="template-upload-device">
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Upload size={20} />}
                  <input type="file" accept="image/*" multiple={maxRefs > 1} className="hidden" onChange={addFromDevice} />
                </label>
              )}
            </div>
          </div>
          )}

          {needsImages && creations.length > 0 && (
            <div>
              <div className="label-mono mb-2">Or pick from My Creations</div>
              <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto">
                {creations.map((c) => (
                  <button key={c.id} onClick={() => addFromCreation(c)} data-testid="template-pick-creation"
                    className="aspect-square rounded-md overflow-hidden border border-zinc-800 hover:border-gold transition-colors">
                    <img src={`data:image/png;base64,${c.image_base64}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {showText && (
            <div>
              <div className="label-mono mb-2">{(needsText ? (template.user_prompt_label && !/optional/i.test(template.user_prompt_label) ? template.user_prompt_label : "Describe what you want") : (template.user_prompt_label || "Optional detail")) + (needsText ? " *" : "")}</div>
              <textarea value={userPrompt} onChange={(e) => setUserPrompt(e.target.value)} data-testid="template-user-prompt" rows={needsText ? 3 : 2}
                placeholder={needsText ? "Type your prompt…" : "Optional…"} className="w-full bg-ink-950 border border-zinc-800 rounded px-3 py-2 text-sm focus:border-gold focus:outline-none resize-none" />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-white/10">
          <button onClick={generate} disabled={busy || uploading} data-testid="template-generate-btn"
            className="w-full flex items-center justify-center gap-2 bg-gold text-ink-950 font-heading font-bold py-3.5 rounded-md hover:bg-goldlight transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
            {template.output_type === "video" ? "Create Video" : "Generate"} · {costLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
